import { ENGINE_VERSION, type AnyWorkoutKind, type ConfigSnapshot, type EventOf, type ExerciseId, type Prescribed, type RepwiseEvent, type SetKind } from './events.ts';
import type { MainLift, WorkoutKind } from './lifts.ts';
import type { Mass } from './mass.ts';
import { floorToLoadable, plateDelta, type Gym, type Loading, type PlateDelta } from './plates.ts';
import { classify, type SessionOutcome } from './progression.ts';
import { warmup } from './warmup.ts';

/**
 * The live workout (US-19, ui_ux spec 03) as a pure state machine with ONE code path: an action becomes
 * events, and state is always produced by applying events. `replaySession(events)` therefore rebuilds a
 * killed session exactly (S08, ADR-0002), and the log is the only source of truth. Time is passed in.
 */

export interface SessionTiming {
  restSuccessS: number;
  restMissS: number;
  restAfterWarmupS: number;
  accessoryRestS: number;
  getReadyS: number;
  undoWindowMs: number;
}

/** Official defaults: 3:00 after a success, 5:00 after a miss (research/01 §3). Other values are house defaults. */
export const DEFAULT_TIMING: SessionTiming = {
  restSuccessS: 180,
  restMissS: 300,
  restAfterWarmupS: 90,
  accessoryRestS: 90,
  getReadyS: 30,
  undoWindowMs: 5000,
};

const MAIN_LIFTS: ReadonlySet<string> = new Set<MainLift>(['squat', 'bench', 'row', 'ohp', 'deadlift']);
export const isMainLift = (e: ExerciseId): e is MainLift => MAIN_LIFTS.has(e);

export interface Slot {
  id: string;
  exercise: ExerciseId;
  /** Plate maths apply (barbell lift). */
  barbell?: boolean;
  /** A hosted plan's set role, for the Set Bar label (top set, back-off, ramp…). */
  role?: 'work' | 'top' | 'backoff' | 'ramp' | 'assistance';
  kind: SetKind;
  /** 0-based within its exercise and kind. */
  index: number;
  /** Count of slots of this exercise and kind. */
  of: number;
  weight: Mass | null;
  reps: number;
}

export interface LogEntry {
  eventId: string;
  reps: number | null;
  weight: Mass | null;
  at: number;
}

export interface Rest {
  startedAt: number;
  /** Base duration from the outcome; `extraS` holds +30 s taps (can be negative, total clamped at 0). */
  baseS: number;
  extraS: number;
  after: 'success' | 'miss' | 'warmup' | 'accessory';
  /** The slot whose log started this rest, so an edit can recalculate it. */
  slotId: string;
}

export type SessionStatus = 'active' | 'finished' | 'ended_early' | 'cancelled';

export interface SessionState {
  workoutId: string;
  kind: AnyWorkoutKind;
  startedAt: number;
  endedAt?: number;
  status: SessionStatus;
  prescriptions: Prescribed[];
  gym: Gym;
  timing: SessionTiming;
  slots: Slot[];
  log: Record<string, LogEntry>;
  /** Slot ids in the order they were logged. */
  order: string[];
  rest: Rest | null;
  awaitingContinue: boolean;
  /** Plates on one side of the bar right now (inner → outer), following minimal changes. */
  bar: Loading;
  /** Last logged set that can still be undone (C7). */
  undoable?: { eventId: string; at: number };
  lastActivityAt: number;
  /** This workout's events, in order. */
  history: RepwiseEvent[];
  /** J20: the exercise the lifter chose to do next; its remaining sets come first. */
  focus?: ExerciseId;
  /** ADR-0025: "Go lighter today" was chosen (the fraction, and the exercises it touched). */
  lightened?: { fraction: number; exercises: ExerciseId[] };
}

export type SessionAction =
  | { type: 'LOG'; id: string; at: number; reps?: number; weight?: Mass }
  | { type: 'UNDO'; id: string; at: number }
  | { type: 'EDIT'; id: string; at: number; slotId: string; reps: number | null; weight?: Mass | null }
  | { type: 'SKIP_WARMUPS'; ids: string[]; at: number }
  | { type: 'SKIP_EXERCISE'; ids: string[]; at: number }
  | { type: 'SKIP_ACCESSORIES'; ids: string[]; at: number }
  | { type: 'REST_ADD'; id: string; at: number; seconds: number }
  | { type: 'REST_SKIP'; id: string; at: number }
  | { type: 'CONTINUE'; id: string; at: number }
  | { type: 'FOCUS'; id: string; at: number; exercise: ExerciseId }
  | { type: 'ADD_EXTRA_SET'; id: string; at: number; exercise: ExerciseId }
  /** ADR-0025: the rest of today's sets, `fraction` lighter (0.1 or 0.2). */
  | { type: 'LIGHTEN'; id: string; at: number; fraction: number }
  | { type: 'FINISH'; id: string; at: number }
  | { type: 'END_EARLY'; id: string; at: number }
  | { type: 'CANCEL'; id: string; at: number };

export interface Step {
  state: SessionState;
  events: RepwiseEvent[];
}

const slotId = (exercise: ExerciseId, kind: SetKind, index: number) => `${exercise}:${kind}:${index}`;

function buildSlots(prescriptions: Prescribed[], gym: Gym): Slot[] {
  const slots: Slot[] = [];
  for (const p of prescriptions) {
    if (p.setPlan) {
      // Hosted plan (ADR-0020): every set has its own weight. Warm-ups run up to the first set's weight for barbell
      // main lifts unless the plan's own sets are the warm-up (Madcow ramps).
      const barbell = p.barbell ?? isMainLift(p.exercise);
      const first = p.setPlan[0]?.weight ?? null;
      const warmLift = isMainLift(p.exercise) ? p.exercise : p.swappedFrom && isMainLift(p.swappedFrom) ? p.swappedFrom : null;
      if (barbell && p.warmup !== false && first !== null && warmLift) {
        const ws = warmup(first, warmLift, gym);
        ws.forEach((w, i) => slots.push({ id: slotId(p.exercise, 'warmup', i), exercise: p.exercise, kind: 'warmup', index: i, of: ws.length, weight: w.weight, reps: w.reps, barbell }));
      }
      p.setPlan.forEach((s, i) => slots.push({ id: slotId(p.exercise, 'work', i), exercise: p.exercise, kind: 'work', index: i, of: p.setPlan!.length, weight: s.weight, reps: s.reps, barbell, role: s.kind }));
      continue;
    }
    const slotLift = isMainLift(p.exercise) ? p.exercise : p.swappedFrom && isMainLift(p.swappedFrom) ? p.swappedFrom : null;
    const barbell = p.barbell ?? isMainLift(p.exercise);
    if (slotLift && barbell && p.weight !== null) {
      const ws = warmup(p.weight, slotLift, gym);
      ws.forEach((w, i) => slots.push({ id: slotId(p.exercise, 'warmup', i), exercise: p.exercise, kind: 'warmup', index: i, of: ws.length, weight: w.weight, reps: w.reps, barbell: true }));
    }
    const kind: SetKind = slotLift ? 'work' : 'accessory';
    for (let i = 0; i < p.sets; i++) slots.push({ id: slotId(p.exercise, kind, i), exercise: p.exercise, kind, index: i, of: p.sets, weight: p.weight, reps: p.reps, barbell: slotLift ? barbell : false });
  }
  return slots;
}

/** The next slot to perform, or undefined when every slot is logged or skipped. */
export function currentSlot(s: SessionState): Slot | undefined {
  const focused = s.focus ? s.slots.find((x) => x.exercise === s.focus && !(x.id in s.log)) : undefined;
  return focused ?? s.slots.find((x) => !(x.id in s.log));
}

/** The weight and reps an extra set repeats: the exercise's last logged working set, else its last planned one. */
export function extraSetFor(s: SessionState, exercise: ExerciseId): { weight: Mass | null; reps: number } | undefined {
  const mine = s.slots.filter((x) => x.exercise === exercise && x.kind !== 'warmup');
  if (!mine.length) return undefined;
  const loggedId = [...s.order].reverse().find((id) => mine.some((x) => x.id === id) && s.log[id]!.reps !== null);
  const slot = loggedId ? mine.find((x) => x.id === loggedId)! : mine.at(-1)!;
  return { weight: loggedId ? s.log[loggedId]!.weight : slot.weight, reps: slot.reps };
}

/** A set counts as hitting target only at the prescribed weight or heavier. */
function hitTarget(slot: Slot, reps: number | null, weight: Mass | null): boolean {
  if (reps === null || reps < slot.reps) return false;
  return slot.weight === null || weight === null || weight >= slot.weight;
}

function restFor(s: SessionState, slot: Slot, entry: LogEntry, extraS = 0): Rest | null {
  if (entry.reps === null) return null;
  const next = currentSlot(s);
  if (!next || next.exercise !== slot.exercise) return null; // none after an exercise's final set
  const t = s.timing;
  const base = { startedAt: entry.at, extraS, slotId: slot.id };
  if (slot.kind === 'warmup') return next.kind === 'work' ? { ...base, baseS: t.restAfterWarmupS, after: 'warmup' } : null;
  if (slot.kind === 'accessory') return { ...base, baseS: t.accessoryRestS, after: 'accessory' };
  const ok = hitTarget(slot, entry.reps, entry.weight);
  return { ...base, baseS: ok ? t.restSuccessS : t.restMissS, after: ok ? 'success' : 'miss' };
}

/** Bar loading after every logged barbell set, replayed from the log in order. */
function barAfter(s: SessionState): Loading {
  let bar: Loading = [];
  for (const id of s.order) {
    const slot = s.slots.find((x) => x.id === id);
    const e = s.log[id];
    if (!slot || !e || e.reps === null || e.weight === null || !slot.barbell) continue;
    const d = plateDelta(s.gym, bar, e.weight);
    if (d) bar = [...d.keep, ...d.add];
  }
  return bar;
}

function awaiting(s: SessionState): boolean {
  const last = s.order[s.order.length - 1];
  const lastSlot = last ? s.slots.find((x) => x.id === last) : undefined;
  const next = currentSlot(s);
  return !!lastSlot && !!next && next.exercise !== lastSlot.exercise;
}

const slotOfEvent = (s: SessionState, eventId: string): string | undefined => Object.keys(s.log).find((k) => s.log[k]!.eventId === eventId);

/** Apply one event. This is the only way state changes. */
function apply(s: SessionState, e: RepwiseEvent): SessionState {
  const base: SessionState = { ...s, history: [...s.history, e], lastActivityAt: e.at };
  switch (e.type) {
    case 'set_logged': {
      if (e.workoutId !== s.workoutId) return s;
      const id = slotId(e.exercise, e.kind, e.index);
      const slot = s.slots.find((x) => x.id === id);
      if (!slot || id in s.log) return s;
      const entry: LogEntry = { eventId: e.id, reps: e.reps, weight: e.weight, at: e.at };
      const next: SessionState = { ...base, log: { ...s.log, [id]: entry }, order: [...s.order, id] };
      return {
        ...next,
        rest: restFor(next, slot, entry),
        awaitingContinue: awaiting(next),
        bar: barAfter(next),
        ...(e.reps !== null ? { undoable: { eventId: e.id, at: e.at } } : {}),
      };
    }
    case 'set_corrected': {
      const id = slotOfEvent(s, e.setEventId);
      if (!id) return s;
      const entry = { ...s.log[id]!, reps: e.reps, weight: e.weight };
      const next: SessionState = { ...base, log: { ...s.log, [id]: entry } };
      const slot = s.slots.find((x) => x.id === id)!;
      // Editing the set that started the running rest recalculates it (3:00 ↔ 5:00), keeping +30 s taps.
      const rest = s.rest && s.rest.slotId === id ? restFor(next, slot, entry, s.rest.extraS) : s.rest;
      return { ...next, rest, bar: barAfter(next) };
    }
    case 'set_voided': {
      const id = slotOfEvent(s, e.setEventId);
      if (!id) return s;
      const { [id]: _gone, ...log } = s.log;
      const next: SessionState = { ...base, log, order: s.order.filter((x) => x !== id) };
      const prevId = next.order[next.order.length - 1];
      const prevSlot = prevId ? s.slots.find((x) => x.id === prevId) : undefined;
      const rest = prevSlot && prevId ? restFor(next, prevSlot, next.log[prevId]!) : null;
      const { undoable: _u, ...rest2 } = next;
      return { ...rest2, rest, awaitingContinue: awaiting(next), bar: barAfter(next) };
    }
    case 'rest_adjusted':
      return s.rest ? { ...base, rest: { ...s.rest, extraS: s.rest.extraS + e.seconds } } : s;
    case 'rest_skipped':
      return { ...base, rest: null };
    case 'transition_continued':
      return { ...base, awaitingContinue: false };
    case 'exercise_focused': {
      if (!s.slots.some((x) => x.exercise === e.exercise)) return s;
      // An explicit choice: no "next exercise" screen; a running rest stays (the lifter still needs it).
      return { ...base, focus: e.exercise, awaitingContinue: false };
    }
    case 'extra_set_added': {
      const like = s.slots.find((x) => x.exercise === e.exercise);
      if (!like) return s;
      const n = s.slots.filter((x) => x.exercise === e.exercise && x.kind === 'extra').length;
      const slot: Slot = { id: slotId(e.exercise, 'extra', n), exercise: e.exercise, kind: 'extra', index: n, of: n + 1, weight: e.weight, reps: e.reps, ...(like.barbell ? { barbell: true } : {}) };
      const slots = [...s.slots.map((x) => (x.exercise === e.exercise && x.kind === 'extra' ? { ...x, of: n + 1 } : x)), slot];
      return { ...base, slots, focus: e.exercise, awaitingContinue: false };
    }
    case 'session_lightened': {
      if (s.lightened) return s; // once per workout
      const slots = s.slots.map((x) => (x.id in s.log || x.weight === null ? x : { ...x, weight: lighter(s.gym, x, e.fraction) }));
      return { ...base, slots, lightened: { fraction: e.fraction, exercises: e.exercises } };
    }
    case 'workout_finished': {
      const { undoable: _u, ...rest } = base;
      return { ...rest, status: e.endedEarly ? 'ended_early' : 'finished', endedAt: e.at, rest: null };
    }
    case 'workout_cancelled': {
      const { undoable: _u, ...rest } = base;
      return { ...rest, status: 'cancelled', endedAt: e.at, rest: null };
    }
    default:
      return s;
  }
}

/**
 * A set `fraction` lighter: barbell sets round down to what the plates make (never below the bar); others round down
 * to a whole unit (dumbbells, machines), never below one.
 */
function lighter(gym: Gym, x: Slot, fraction: number): Mass {
  const raw = Math.floor(x.weight! * (1 - fraction));
  if (x.barbell) return Math.max(floorToLoadable(gym, raw) ?? gym.bar, gym.bar);
  return Math.max(100, Math.floor(raw / 100) * 100);
}

/** The exercises "Go lighter today" would touch: any with a weighted work or extra set still to do. */
export function lightenable(s: SessionState): ExerciseId[] {
  return [...new Set(s.slots.filter((x) => !(x.id in s.log) && x.weight !== null && (x.kind === 'work' || x.kind === 'extra' || x.kind === 'accessory')).map((x) => x.exercise))];
}

function emptySession(e: EventOf<'workout_started'>, timing: SessionTiming): SessionState {
  return {
    workoutId: e.workoutId,
    kind: e.kind,
    startedAt: e.at,
    status: 'active',
    prescriptions: e.prescriptions,
    gym: e.config.gym,
    timing,
    slots: buildSlots(e.prescriptions, e.config.gym),
    log: {},
    order: [],
    rest: null,
    awaitingContinue: false,
    bar: [],
    lastActivityAt: e.at,
    history: [e],
  };
}

export function startSession(input: {
  id: string;
  workoutId: string;
  kind: AnyWorkoutKind;
  at: number;
  prescriptions: Prescribed[];
  config: ConfigSnapshot;
  timing?: SessionTiming;
  /** A hosted-plan workout (ADR-0020). */
  plan?: import('./events.ts').PlanRef;
}): Step {
  const ev: EventOf<'workout_started'> = {
    type: 'workout_started',
    id: input.id,
    at: input.at,
    workoutId: input.workoutId,
    kind: input.kind,
    engineVersion: ENGINE_VERSION,
    prescriptions: input.prescriptions,
    config: input.config,
    ...(input.plan ? { plan: input.plan } : {}),
  };
  return { state: emptySession(ev, input.timing ?? input.config.timing ?? DEFAULT_TIMING), events: [ev] };
}

/**
 * Rebuild a session from the event log (resume after a kill, or on another device). Takes the whole log
 * or just this workout's events; corrections and voids are matched by the set event they reference.
 */
export function replaySession(events: readonly RepwiseEvent[], workoutId: string, timing?: SessionTiming): SessionState | undefined {
  const start = events.find((e): e is EventOf<'workout_started'> => e.type === 'workout_started' && e.workoutId === workoutId);
  if (!start) return undefined;
  let s = emptySession(start, timing ?? start.config.timing ?? DEFAULT_TIMING);
  // Only this workout's events, from its start on. Other workouts' finishes, rests and continues must never touch
  // it (regression 13 Oct 2026: a previous workout's finish ended every later session on replay).
  const from = events.indexOf(start);
  for (let i = from + 1; i < events.length; i++) {
    const e = events[i]!;
    if ('workoutId' in e && e.workoutId !== workoutId) continue;
    s = apply(s, e);
  }
  return s;
}

function setEvent(s: SessionState, slot: Slot, id: string, at: number, reps: number | null, weight: Mass | null): EventOf<'set_logged'> {
  return { type: 'set_logged', id, at, workoutId: s.workoutId, exercise: slot.exercise, kind: slot.kind, index: slot.index, reps, weight };
}

function eventsFor(s: SessionState, a: SessionAction): RepwiseEvent[] {
  const w = s.workoutId;
  const skip = (ids: string[], at: number, pred: (x: Slot) => boolean) => {
    const targets = s.slots.filter((x) => !(x.id in s.log) && pred(x));
    if (ids.length < targets.length) throw new RangeError(`Need ${targets.length} event ids, got ${ids.length}`);
    return targets.map((slot, i) => setEvent(s, slot, ids[i]!, at, null, null));
  };
  const cur = currentSlot(s);
  switch (a.type) {
    case 'LOG':
      return cur ? [setEvent(s, cur, a.id, a.at, a.reps ?? cur.reps, a.weight ?? cur.weight)] : [];
    case 'UNDO':
      return s.undoable && a.at - s.undoable.at <= s.timing.undoWindowMs ? [{ type: 'set_voided', id: a.id, at: a.at, setEventId: s.undoable.eventId }] : [];
    case 'EDIT': {
      const prev = s.log[a.slotId];
      const slot = s.slots.find((x) => x.id === a.slotId);
      if (!prev || !slot) return [];
      const weight = a.weight === undefined ? (prev.weight ?? slot.weight) : a.weight;
      return [{ type: 'set_corrected', id: a.id, at: a.at, setEventId: prev.eventId, reps: a.reps, weight }];
    }
    case 'SKIP_WARMUPS':
      return cur && cur.kind === 'warmup' ? skip(a.ids, a.at, (x) => x.exercise === cur.exercise && x.kind === 'warmup') : [];
    case 'SKIP_EXERCISE':
      return cur ? skip(a.ids, a.at, (x) => x.exercise === cur.exercise) : [];
    case 'SKIP_ACCESSORIES':
      return skip(a.ids, a.at, (x) => x.kind === 'accessory');
    case 'REST_ADD': {
      if (!s.rest) return [];
      const total = s.rest.baseS + s.rest.extraS;
      const seconds = Math.max(a.seconds, -total); // never below zero
      return seconds === 0 ? [] : [{ type: 'rest_adjusted', id: a.id, at: a.at, workoutId: w, seconds }];
    }
    case 'REST_SKIP':
      return s.rest ? [{ type: 'rest_skipped', id: a.id, at: a.at, workoutId: w }] : [];
    case 'CONTINUE':
      return s.awaitingContinue ? [{ type: 'transition_continued', id: a.id, at: a.at, workoutId: w }] : [];
    case 'FOCUS':
      return s.slots.some((x) => x.exercise === a.exercise && !(x.id in s.log)) ? [{ type: 'exercise_focused', id: a.id, at: a.at, workoutId: w, exercise: a.exercise }] : [];
    case 'ADD_EXTRA_SET': {
      const like = extraSetFor(s, a.exercise);
      return like ? [{ type: 'extra_set_added', id: a.id, at: a.at, workoutId: w, exercise: a.exercise, ...like }] : [];
    }
    case 'LIGHTEN': {
      const exercises = lightenable(s);
      return s.lightened || !exercises.length || !(a.fraction > 0 && a.fraction < 1) ? [] : [{ type: 'session_lightened', id: a.id, at: a.at, workoutId: w, fraction: a.fraction, exercises }];
    }
    case 'FINISH':
      return cur ? [] : [{ type: 'workout_finished', id: a.id, at: a.at, workoutId: w, endedEarly: false }];
    case 'END_EARLY': {
      // Ending before anything real was logged is a cancel: it must not advance A/B or reset the break clock.
      const anyDone = s.slots.some((x) => (x.kind === 'work' || x.kind === 'accessory') && s.log[x.id]?.reps != null);
      return anyDone ? [{ type: 'workout_finished', id: a.id, at: a.at, workoutId: w, endedEarly: true }] : [{ type: 'workout_cancelled', id: a.id, at: a.at, workoutId: w }];
    }
    case 'CANCEL':
      return [{ type: 'workout_cancelled', id: a.id, at: a.at, workoutId: w }];
  }
}

export function reduce(s: SessionState, a: SessionAction): Step {
  if (s.status !== 'active') return { state: s, events: [] };
  const events = eventsFor(s, a);
  return { state: events.reduce(apply, s), events };
}

export type Phase = 'warmup' | 'work' | 'rest' | 'transition' | 'accessory' | 'finish' | 'ended';

export interface RestView {
  targetS: number;
  elapsedS: number;
  remainingS: number;
  overtimeS: number;
  getReady: boolean;
  /** Rest target reached: the UI may return to the set (S04 → S03) or show overtime (C5). */
  done: boolean;
  endsAt: number;
  after: Rest['after'];
}

export interface LiftResult {
  /** Any exercise with a weight (hosted plans add more than the five main lifts). */
  lift: ExerciseId;
  weight: Mass;
  reps: (number | null)[];
  outcome: SessionOutcome;
}

export interface SessionView {
  phase: Phase;
  slot?: Slot;
  /** Working sets of the current exercise for the set circles (C2). */
  circles: { reps: number | null; state: 'done' | 'missed' | 'skipped' | 'current' | 'upcoming' }[];
  rest?: RestView;
  /** Plates now on the bar and the minimal change to the current set's weight (C4/C8). */
  loading?: { bar: Loading; delta: PlateDelta | null };
  /** S05: the exercise just finished and what comes next. */
  transition?: { done?: LiftResult; next: Prescribed };
  /** "Set 3: 5 reps · Edit" (S04). */
  lastLogged?: { slot: Slot; entry: LogEntry };
  elapsedS: number;
  /** For S08: show the full resume screen only after a long gap (spec: > 2 h). */
  sinceLastActivityS: number;
  canUndo: boolean;
  undoRemainingMs: number;
}

/** Everything the UI needs at time `now`; pure, so resume after a kill is exact (S08). */
export function view(s: SessionState, now: number): SessionView {
  const elapsedS = Math.max(0, Math.floor(((s.endedAt ?? now) - s.startedAt) / 1000));
  const undoRemainingMs = s.undoable ? Math.max(0, s.timing.undoWindowMs - (now - s.undoable.at)) : 0;
  const lastId = s.order[s.order.length - 1];
  const lastSlot = lastId ? s.slots.find((x) => x.id === lastId) : undefined;
  const base = {
    elapsedS,
    sinceLastActivityS: Math.max(0, Math.floor((now - s.lastActivityAt) / 1000)),
    canUndo: undoRemainingMs > 0,
    undoRemainingMs,
    ...(lastSlot && lastId ? { lastLogged: { slot: lastSlot, entry: s.log[lastId]! } } : {}),
  };
  const slot = currentSlot(s);
  if (s.status !== 'active') return { phase: 'ended', circles: [], ...base };
  if (!slot) return { phase: 'finish', circles: [], ...base };

  const circleKind: SetKind = slot.kind === 'warmup' ? 'work' : slot.kind;
  const circles = s.slots
    .filter((x) => x.exercise === slot.exercise && x.kind === circleKind)
    .map((x) => {
      const e = s.log[x.id];
      if (!e) return { reps: null, state: x.id === slot.id ? ('current' as const) : ('upcoming' as const) };
      if (e.reps === null) return { reps: null, state: 'skipped' as const };
      return { reps: e.reps, state: hitTarget(x, e.reps, e.weight) ? ('done' as const) : ('missed' as const) };
    });

  const loading = slot.barbell && slot.weight !== null ? { bar: s.bar, delta: plateDelta(s.gym, s.bar, slot.weight) } : undefined;
  let phase: Phase = slot.kind === 'warmup' ? 'warmup' : slot.kind === 'accessory' ? 'accessory' : 'work'; // an extra set runs as a set
  let transition: SessionView['transition'];
  if (s.awaitingContinue) {
    phase = 'transition';
    const next = s.prescriptions.find((p) => p.exercise === slot.exercise)!;
    const done = lastSlot && lastSlot.kind !== 'warmup' ? results(s).find((r) => r.lift === lastSlot.exercise) : undefined;
    transition = { next, ...(done ? { done } : {}) };
  }
  let rest: RestView | undefined;
  if (s.rest && !s.awaitingContinue) {
    const targetS = Math.max(0, s.rest.baseS + s.rest.extraS);
    const elapsed = Math.max(0, Math.floor((now - s.rest.startedAt) / 1000));
    const remainingS = Math.max(0, targetS - elapsed);
    rest = {
      targetS,
      elapsedS: elapsed,
      remainingS,
      overtimeS: Math.max(0, elapsed - targetS),
      getReady: remainingS > 0 && remainingS <= s.timing.getReadyS,
      done: remainingS === 0,
      endsAt: s.rest.startedAt + targetS * 1000,
      after: s.rest.after,
    };
    phase = 'rest';
  }
  return { phase, slot, circles, ...(rest ? { rest } : {}), ...(loading ? { loading } : {}), ...(transition ? { transition } : {}), ...base };
}

/** Per-lift outcome of the working sets (warm-ups excluded). Unlogged sets count as not done (review M6). */
export function results(s: SessionState): LiftResult[] {
  return s.prescriptions
    .filter((p) => p.weight !== null && (p.setPlan !== undefined || isMainLift(p.exercise) || (!!p.swappedFrom && isMainLift(p.swappedFrom))))
    .map((p) => {
      const slots = s.slots.filter((x) => x.exercise === p.exercise && x.kind === 'work');
      const reps = slots.map((x) => {
        const e = s.log[x.id];
        if (!e || e.reps === null) return null;
        return hitTarget(x, e.reps, e.weight) ? e.reps : Math.min(e.reps, x.reps - 1);
      });
      // Hosted plans: each set has its own target (ramps, a triple, a set of 8); success = every set hit its own.
      const outcome: SessionOutcome = p.setPlan
        ? reps.every((r) => r === null) ? 'skipped' : reps.every((r, i) => r !== null && r >= slots[i]!.reps) ? 'success' : 'miss'
        : classify(reps, p.sets, p.reps);
      return { lift: p.exercise, weight: p.weight!, reps, outcome };
    });
}
